import type { ClubCollection, ClubCollections } from '@data';
import { Inline, Stack, Text, u } from '@ui';

import * as css from './CompactClubTable.css';

type CompactClubTableProps = {
    clubCollections: ClubCollections;
    displaySelectedClubId?: number;
    displaySelectedClubIds?: number[];
    onClick: (id: number) => void;
    canSelectClubGroup?: boolean;
    selectedClubGroup?: string;
    onClubGroupClick?: (clubGroupName: string, clubTypeId: number) => void;
    noClubSelected?: boolean;
    onNoClubClick?: () => void;
    wrap?: boolean;
};

export const CompactClubTable = ({
    clubCollections,
    displaySelectedClubId,
    displaySelectedClubIds,
    onClick,
    canSelectClubGroup = false,
    selectedClubGroup,
    onClubGroupClick,
    noClubSelected = false,
    onNoClubClick,
    wrap = false,
}: CompactClubTableProps) => {
    const defaultSelectedClubIds = displaySelectedClubId ? [displaySelectedClubId] : [];
    const selectedClubIds = selectedClubGroup ? [] : displaySelectedClubIds ?? defaultSelectedClubIds;
    const hasSelectedClub = selectedClubIds.length > 0;
    const visibleClubGroups = clubCollections.filter(({ clubs }) => clubs.length > 0);
    const multiClubGroups = visibleClubGroups.filter(({ clubs }) => clubs.length > 1);
    const singleClubGroups = visibleClubGroups.filter(({ clubs }) => clubs.length === 1);

    return (
        <Stack gap={u(3)}>
            {multiClubGroups.map(({ typeName, id, clubs }) => {
                return (
                    <Stack key={id} gap={u(2)}>
                        {canSelectClubGroup ? (
                            <button
                                type="button"
                                className={css.clubGroupButton({
                                    selected: !hasSelectedClub && selectedClubGroup === typeName,
                                })}
                                onClick={() => onClubGroupClick?.(typeName, id)}
                                aria-pressed={!hasSelectedClub && selectedClubGroup === typeName}
                            >
                                <Text variant="label" as="span" color="inherit">
                                    {typeName}
                                </Text>
                            </button>
                        ) : (
                            <Text variant="label" as="p" color="muted">
                                {typeName}
                            </Text>
                        )}

                        <ClubButtons clubs={clubs} selectedClubIds={selectedClubIds} onClick={onClick} wrap={wrap} />
                    </Stack>
                );
            })}
            {singleClubGroups.length > 0 && (
                <Inline gap={u(3)} flexWrap="wrap">
                    {singleClubGroups.map(({ typeName, id, clubs }) => {
                        return (
                            <Stack key={id} gap={u(2)} alignItems="center">
                                <Text variant="label" as="p" color="muted">
                                    {typeName}
                                </Text>
                                <ClubButtons
                                    clubs={clubs}
                                    selectedClubIds={selectedClubIds}
                                    onClick={onClick}
                                    wrap={wrap}
                                />
                            </Stack>
                        );
                    })}
                </Inline>
            )}
            {onNoClubClick && (
                <button
                    type="button"
                    className={css.clubGroupButton({ selected: noClubSelected })}
                    onClick={onNoClubClick}
                    aria-pressed={noClubSelected}
                >
                    <Text variant="h3" whiteSpace="nowrap" color="inherit">
                        No Club
                    </Text>
                </button>
            )}
        </Stack>
    );
};

type ClubButtonsProps = {
    clubs: ClubCollection['clubs'];
    selectedClubIds: number[];
    onClick: (id: number) => void;
    wrap: boolean;
};

const ClubButtons = ({ clubs, selectedClubIds, onClick, wrap }: ClubButtonsProps) => {
    return (
        <Inline
            gap={u(2)}
            alignItems="center"
            style={{ overflowX: 'auto' }}
            flexWrap={wrap ? 'wrap' : { sm: 'wrap', md: 'nowrap' }}
        >
            {clubs.map(({ id, longName, shortName }) => {
                const isSelected = selectedClubIds.includes(id);

                return (
                    <button
                        key={id}
                        type="button"
                        className={css.clubPill({ selected: isSelected })}
                        onClick={() => onClick(id)}
                        aria-pressed={isSelected}
                        aria-label={longName}
                    >
                        <Text variant="h3" color="inherit">
                            {shortName}
                        </Text>
                    </button>
                );
            })}
        </Inline>
    );
};
